import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import {
  getCommentChildren,
  getComment
} from '../../store/selectors/comments/commentsSelectors';
import WithCommentsFromStore from '../../HOCs/WithCommentsFromStore/WithCommentsFromStore';
import RepliesLoadingAnimation from '../../components/RepliesLoadingAnimation/RepliesLoadingAnimation';

import Comment from '../CommentContainer/CommentContainer';
import { sortComments } from '../../store/utils/utils';
import '../../components/RepliesCollapse/RepliesCollapse.css';
import { getCommentsContext } from '../../store/selectors/settings/settingsSelectors';

const Reply = WithCommentsFromStore(Comment);

export const RepliesContainer = ({
  id: parentId,
  replies,
  hasRepliesCollapse,
  repliesCollapsed,
  fetchingReplies,
  childrenIds,
  renderLoadingAnimation,
  renderLoadMoreButton,
  commentsContext
}) => {
  const isOldestToNewest = true;
  const sortedReplies = sortComments(replies, isOldestToNewest);

  return (
    <>
      {fetchingReplies
        ? childrenIds.map((id) => <RepliesLoadingAnimation key={id} />)
        : sortedReplies.map(
            (
              {
                id: replyId,
                recentlyAddedByUser,
                community_id: replyCommunityId
              },
              index,
              self
            ) => {
              // Only want to render LoadingAnimation or ViewMoreButton before the comment
              // if it is the first recently added comment
              const isFirstRecentlyAddedComment = Boolean(
                self.findIndex((item) => item.recentlyAddedByUser) === index
              );
              const isLastElement = index === sortedReplies.length - 1;
              return (
                <>
                  {/* LoadingAnimation and LoadMoreButton components are still in charge of 
                      whether or not they should render. 
                      Here we are checking if the rendering should be done before a comment. 
                      This means we want animations, errors and the load more button to render between
                      comments that are not marked as recentlyAddedByUser and comments that are
                  */}
                  {isFirstRecentlyAddedComment && renderLoadingAnimation()}
                  {isFirstRecentlyAddedComment && renderLoadMoreButton()}
                  <Reply
                    // This testID is used to check the order of components
                    testID="reply-component"
                    key={replyId}
                    id={replyId}
                    parentId={parentId}
                    hasRepliesCollapse={hasRepliesCollapse}
                    repliesCollapsed={repliesCollapsed}
                    // This prop is used to assign
                    // descending z-indices to comment replies
                    // so the ellipsis menu doesn't overlap
                    // another ellipsis icon
                    replyZIndex={sortedReplies.length - index}
                    communityId={replyCommunityId}
                    commentsContext={commentsContext}
                  />
                  {/* LoadingAnimation and LoadMoreButton components are still in charge of 
                      whether or not they should render. 
                      Here we are checking if the rendering should be done after a comment. 
                      This means we want animations, errors and the load more button to 
                      render after all comments
                  */}
                  {!recentlyAddedByUser &&
                    isLastElement &&
                    renderLoadingAnimation()}
                  {!recentlyAddedByUser &&
                    isLastElement &&
                    renderLoadMoreButton()}
                </>
              );
            }
          )}
    </>
  );
};

RepliesContainer.propTypes = {
  id: PropTypes.number.isRequired,
  replies: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  hasRepliesCollapse: PropTypes.bool,
  repliesCollapsed: PropTypes.bool,
  fetchingReplies: PropTypes.bool,
  childrenIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  renderLoadingAnimation: PropTypes.func,
  renderLoadMoreButton: PropTypes.func,
  commentsContext: PropTypes.string
};

RepliesContainer.defaultProps = {
  hasRepliesCollapse: false,
  repliesCollapsed: false,
  fetchingReplies: false,
  renderLoadingAnimation: () => {},
  renderLoadMoreButton: () => {},
  commentsContext: null
};

const mapStateToProps = (state, { id }) => {
  const parentComment = getComment(state, id);
  const {
    descendant_ids: childrenIds,
    childLoading: { loading: fetchingReplies } = {}
  } = parentComment;
  return {
    childrenIds,
    fetchingReplies,
    replies: getCommentChildren(state, id) || [],
    commentsContext: getCommentsContext(state)
  };
};

export default connect(mapStateToProps)(RepliesContainer);
